import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import TaskExamCard from "@/components/studio/TaskExamCard";
import { useAuth } from "@/context/AuthContext";
import AppNav from "@/components/AppNav";
import { SlideCarousel } from "@/components/studio/SlideCarousel";
import UpgradeCard from "@/components/trial/UpgradeCard";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FileText, CheckCircle2, Loader2, ArrowRight, Award, Sparkles, GraduationCap, ClipboardCheck, Lock, Trophy, Presentation, Upload, ExternalLink, Trash2, Target, Flame } from "lucide-react";

const GRADE_COLOR = { A: "#34D399", B: "#22D3EE", C: "#FB923C", D: "#F59E0B", F: "#E11D48" };
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function StudioHub() {
  const { track } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isGuide = user?.role === "guide";
  const isTrial = !!user?.trial;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lockedMsg, setLockedMsg] = useState(null);
  const [assignedIds, setAssignedIds] = useState([]);

  useEffect(() => {
    setLoading(true);
    setLockedMsg(null);
    api.get(`/studio/${track}`).then((r) => setData(r.data))
      .catch((e) => { const d = e?.response?.data?.detail; if (e?.response?.status === 403 && d?.code) { setData(null); setLockedMsg(d.message); } })
      .finally(() => setLoading(false));
  }, [track]);

  useEffect(() => {
    if (isGuide) { setAssignedIds([]); return; }
    api.get("/me/assignments")
      .then((r) => setAssignedIds((r.data || []).filter((a) => a.track === track).flatMap((a) => a.mission_ids)))
      .catch(() => setAssignedIds([]));
  }, [track, isGuide]);

  const [checkpoints, setCheckpoints] = useState([]);
  const [finalMeta, setFinalMeta] = useState(null);
  const [unitMeta, setUnitMeta] = useState(null);
  const [blockSlides, setBlockSlides] = useState({});
  const [deckImages, setDeckImages] = useState({});
  const [editingBlock, setEditingBlock] = useState(null);
  const [urlInput, setUrlInput] = useState("");
  const [uploadingBlock, setUploadingBlock] = useState(null);
  const [unitTask, setUnitTask] = useState(null);
  const [midTask, setMidTask] = useState(null);
  const [streak, setStreak] = useState({ current: 0, best: 0 });
  useEffect(() => {
    api.get("/studio/drill-streak").then((r) => setStreak(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    api.get(`/assessments/track/${track}`).then((r) => setCheckpoints(r.data.checkpoints || [])).catch(() => setCheckpoints([]));
    api.get(`/assessments/final/meta`).then((r) => setFinalMeta(r.data)).catch(() => setFinalMeta(null));
    api.get(`/assessments/unit/${track}/meta`).then((r) => setUnitMeta(r.data)).catch(() => setUnitMeta(null));
    api.get(`/unit-task/${track}-unittask`).then((r) => setUnitTask(r.data)).catch(() => setUnitTask(null));
    api.get(`/unit-task/${track}-midtask`).then((r) => setMidTask(r.data)).catch(() => setMidTask(null));
    api.get(`/block-slides/${track}`).then((r) => setBlockSlides(r.data || {})).catch(() => setBlockSlides({}));
  }, [track]);

  const refreshBlockSlides = () => api.get(`/block-slides/${track}`).then((r) => setBlockSlides(r.data || {})).catch(() => {});

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL || ""}/decks/img/manifest.json`)
      .then((r) => (r.ok ? r.json() : {}))
      .then((m) => setDeckImages(m || {}))
      .catch(() => setDeckImages({}));
  }, []);

  const saveBlockSlides = async (blockId, url) => {
    try {
      await api.put(`/block-slides/${blockId}`, { embed_url: url });
      await refreshBlockSlides();
      setEditingBlock(null);
      toast.success(url ? "Slides linked!" : "Slides link removed.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Please paste a valid published slides embed link."); }
  };

  const uploadPptx = async (blockId, file) => {
    if (!file) return;
    if (!/\.pptx?$/i.test(file.name)) { toast.error("Please choose a slide deck (.pptx or .ppt)."); return; }
    setUploadingBlock(blockId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/block-slides/${blockId}/upload-pptx`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshBlockSlides();
      setEditingBlock(null);
      if (res.data?.pptx?.status === "ready") toast.success("Slide deck converted — students will see your slides.");
      else { toast.success("Slide deck uploaded — converting slides…"); pollPptx(blockId); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed. Please try again."); }
    finally { setUploadingBlock(null); }
  };

  const pollPptx = (blockId, tries = 0) => {
    if (tries > 12) return;
    setTimeout(async () => {
      try {
        const r = await api.get(`/block-slides/${blockId}/pptx-status`);
        if (r.data?.status === "ready") { await refreshBlockSlides(); toast.success("Slides are ready!"); return; }
      } catch (e) { /* keep trying */ }
      pollPptx(blockId, tries + 1);
    }, 3000);
  };

  const deletePptx = async (blockId) => {
    try {
      await api.delete(`/block-slides/${blockId}/pptx`);
      await refreshBlockSlides();
      toast.success("Removed your slide deck — showing the starter deck.");
    } catch (e) { toast.error("Could not remove the slide deck."); }
  };

  if (loading) return (<div className="min-h-screen"><AppNav /><div className="flex justify-center py-40"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></div>);
  if (!data) return (
    <div className="min-h-screen"><AppNav />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <button data-testid="studio-hub-back-btn" onClick={() => navigate("/studio/design")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"><ArrowLeft className="w-4 h-4" /> Back to Graphic Design</button>
        {lockedMsg && <p data-testid="studio-trial-locked-msg" className="text-sm text-slate-300 mb-6 flex items-center gap-2"><Lock className="w-4 h-4 text-primary" /> {lockedMsg}</p>}
        {lockedMsg ? <UpgradeCard user={user} /> : <p className="text-muted-foreground">This unit could not be loaded.</p>}
      </div>
    </div>
  );

  const missions = [...data.missions].sort((a, b) => a.order - b.order);
  const progress = data.progress || {};
  const blockTasks = data.block_tasks || [];
  const drills = data.drills || [];
  const midTests = data.mid_tests || [];
  const locks = data.locks || { blocks: {}, missions: {}, tests: {} };
  const mLocked = (id) => !isGuide && !!locks.missions?.[id];
  const tLocked = (id) => !isGuide && !!locks.tests?.[id];
  const LOCK_MSG = "Locked by your Guide — not open yet";
  const mastered = missions.filter((m) => progress[m.id]?.mastery).length;
  const firstUndone = missions.find((m) => !progress[m.id]?.mastery && !mLocked(m.id)) || missions[0];

  const missionCard = (m, i) => {
    const p = progress[m.id];
    const isCapstone = m.points > 100;
    const locked = mLocked(m.id);
    return (
      <button key={m.id} data-testid={`studio-mission-card-${m.id}`} onClick={() => locked ? toast.info(LOCK_MSG) : navigate(`/studio/${track}/${m.id}`)} style={{ animationDelay: `${i * 0.03}s` }}
        aria-disabled={locked} title={locked ? LOCK_MSG : undefined}
        className={`hq-fade-up text-left hq-glass rounded-2xl p-5 border transition-all ${locked ? "border-white/5 opacity-50 grayscale cursor-not-allowed" : "border-white/10 hover:border-[#22D3EE]/50 hover:-translate-y-0.5"}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 font-mono-data ${isCapstone ? "bg-primary/15 text-primary" : "bg-[#22D3EE]/15 text-[#22D3EE]"}`}>
              {locked ? <Lock className="w-4 h-4" data-testid={`mission-lock-${m.id}`} /> : isCapstone ? <Award className="w-5 h-5" /> : m.order}
            </div>
            <div className="min-w-0">
              <h3 className="font-display text-lg leading-tight truncate flex items-center gap-2">{m.title}{isCapstone && <Sparkles className="w-4 h-4 text-primary shrink-0" />}</h3>
              <div className="flex items-center gap-2 mt-0.5">
                <p className="text-xs text-muted-foreground truncate">{m.chunk}</p>
                {assignedIds.includes(m.id) && <span data-testid={`assigned-badge-${m.id}`} className="text-[10px] font-semibold uppercase tracking-wide bg-[#FB923C]/20 text-[#FB923C] rounded px-1.5 py-0.5 shrink-0">Assigned</span>}
              </div>
              <p className="text-[11px] text-slate-500 mt-1">{m.tasks.length} tasks · {m.points} pts</p>
            </div>
          </div>
          {p && (
            <div className="text-right shrink-0">
              <span className="font-display text-2xl" style={{ color: GRADE_COLOR[p.grade] }}>{p.grade}</span>
              {p.mastery && <CheckCircle2 className="w-4 h-4 text-emerald-400 ml-auto mt-1" />}
            </div>
          )}
        </div>
      </button>
    );
  };

  return (
    <div className="min-h-screen">
      <AppNav />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {!(isTrial && !isGuide) && (
          <button data-testid="studio-hub-back-btn" onClick={() => navigate(isGuide ? "/guide" : "/map")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6">
            <ArrowLeft className="w-4 h-4" /> {isGuide ? "Back to the console" : "Back to the map"}
          </button>
        )}

        {isGuide && (
          <div data-testid="studio-guide-preview-banner" className="hq-glass rounded-xl px-4 py-3 mb-5 flex items-center gap-3 border border-[#22D3EE]/30">
            <GraduationCap className="w-5 h-5 text-[#22D3EE] shrink-0" />
            <p className="text-sm text-slate-200">Teaching preview — try any mission just like your Explorers do. Your attempts here are <span className="text-[#22D3EE]">not graded or saved</span> to any gradebook.</p>
          </div>
        )}

        <div className="hq-fade-up flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest font-mono-data text-[#22D3EE]">Skill Studio</p>
            <h1 data-testid="studio-hub-title" className="font-display text-4xl sm:text-5xl mt-1 tracking-tight">{data.track.name}</h1>
            <p className="text-slate-300 mt-2 max-w-xl">{data.track.intro}</p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {!isGuide && (
              <div data-testid="drill-streak-card" className="hq-glass rounded-2xl px-5 py-4 text-center" title="Score 100% on skill drills back to back. Every 3 in a row earns +50 bonus points, every 5 earns +100. A drill under 100% resets the streak.">
                <p className={`font-mono-data text-3xl flex items-center justify-center gap-1.5 ${streak.current > 0 ? "text-[#FB923C]" : "text-slate-500"}`}>
                  <Flame className={`w-6 h-6 ${streak.current > 0 ? "text-[#FB923C]" : "text-slate-600"}`} />
                  <span data-testid="drill-streak-current">{streak.current}</span>
                </p>
                <p className="text-xs text-muted-foreground mt-1">Drill streak{streak.best > 0 ? <span data-testid="drill-streak-best"> · best {streak.best}</span> : null}</p>
              </div>
            )}
            <div className="hq-glass rounded-2xl px-5 py-4 text-center">
              <p className="font-mono-data text-3xl text-primary">{mastered}/{missions.length}</p>
              <p className="text-xs text-muted-foreground mt-1">Missions mastered</p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-6">
          {[{ id: "design", name: "Graphic Design" }, { id: "slides", name: "Presentations" }, { id: "docs", name: "Word Processing" }, { id: "sheets", name: "Spreadsheets" }, { id: "email", name: "Email & Communication" }].map((t) => (
            <button
              key={t.id}
              data-testid={`studio-track-pill-${t.id}`}
              onClick={() => navigate(`/studio/${t.id}`)}
              className={`px-4 py-1.5 rounded-full text-sm transition-colors border inline-flex items-center gap-1.5 ${
                t.id === track ? "bg-[#22D3EE] text-[#04121f] border-[#22D3EE]" : "border-white/15 text-slate-300 hover:border-[#22D3EE]/50"
              }`}
            >
              {isTrial && t.id !== "docs" && <Lock className="w-3.5 h-3.5 opacity-70" />}{t.name}
            </button>
          ))}
        </div>

        <Button data-testid="studio-continue-btn" onClick={() => navigate(`/studio/${track}/${firstUndone.id}`)} className="mt-6 bg-[#22D3EE] text-[#04121f] hover:bg-[#67E8F9] hq-glow-teal">
          {mastered === 0 ? "Start Mission 1" : "Continue"} · {firstUndone.title} <ArrowRight className="w-4 h-4 ml-2" />
        </Button>

        {(() => {
          const blocks = checkpoints
            .map((c) => ({ cp: c, missions: missions.filter((m) => c.covers.includes(m.id)).sort((a, b) => a.order - b.order) }))
            .filter((b) => b.missions.length > 0);
          if (blocks.length === 0) {
            return <div className="grid gap-3 sm:grid-cols-2 mt-8">{missions.map((m, i) => missionCard(m, i))}</div>;
          }
          return (
            <div className="mt-8 space-y-10">
              {blocks.map((b, bi) => {
                const c = b.cp;
                const noAttempts = c.attempts_used >= c.max_attempts;
                const remaining = Math.max(0, c.max_attempts - c.attempts_used);
                const bs = blockSlides[c.id] || {};
                const url = bs.embed_url || "";
                const pptx = bs.pptx || null;
                const first = b.missions[0].order, last = b.missions[b.missions.length - 1].order;
                const blockLocked = !isGuide && !!locks.blocks?.[c.id];
                return (
                  <div key={c.id}>
                  <section data-testid={`block-${c.id}`} className={blockLocked ? "opacity-80" : ""}>
                    <h2 className="font-display text-2xl flex items-center gap-3 flex-wrap">Block {bi + 1} <span className="text-sm text-muted-foreground">· Lessons {first}–{last}</span>
                      {blockLocked && <span data-testid={`block-locked-${c.id}`} className="inline-flex items-center gap-1.5 text-xs font-sans font-normal px-2.5 py-1 rounded-full border border-[#FB923C]/40 bg-[#FB923C]/10 text-[#FB923C]"><Lock className="w-3.5 h-3.5" /> {LOCK_MSG}</span>}
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1 mb-3">{blockLocked ? "Your Guide will open this block when the class is ready. You can preview what's coming below." : `Watch the teaching slides, complete the ${b.missions.length} skills, then take the checkpoint.`}</p>

                    <div className="hq-glass rounded-2xl p-4 mb-4 border border-white/10">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <p className="text-sm font-medium flex items-center gap-2"><Presentation className="w-4 h-4 text-[#818CF8]" /> Teaching Slides</p>
                        {isGuide && <button data-testid={`slides-edit-${c.id}`} onClick={() => { setEditingBlock(editingBlock === c.id ? null : c.id); setUrlInput(url || ""); }} className="text-xs text-[#a5b4fc] hover:underline">{editingBlock === c.id ? "Close" : "Manage slides"}</button>}
                      </div>
                      {isGuide && editingBlock === c.id && (
                        <div className="mb-3 space-y-3 rounded-lg bg-slate-900/50 border border-white/10 p-3">
                          <div>
                            <p className="text-xs text-slate-300 mb-1.5">Option 1 · Paste a published <span className="text-[#a5b4fc]">online slides</span> link (fully editable, live-updating)</p>
                            <div className="flex flex-col sm:flex-row gap-2">
                              <input data-testid={`slides-input-${c.id}`} value={urlInput} onChange={(e) => setUrlInput(e.target.value)} placeholder="https://docs.google.com/presentation/d/.../embed" className="flex-1 h-9 rounded-md bg-slate-800 border border-slate-600 px-2 text-sm text-white placeholder:text-slate-400 outline-none focus:border-[#818CF8]" />
                              <Button size="sm" data-testid={`slides-save-${c.id}`} onClick={() => saveBlockSlides(c.id, urlInput.trim())} className="bg-[#22D3EE] text-[#04121f] hover:bg-[#67E8F9]">Save link</Button>
                              {url && <Button size="sm" variant="outline" data-testid={`slides-remove-link-${c.id}`} onClick={() => saveBlockSlides(c.id, "")}>Remove link</Button>}
                            </div>
                          </div>
                          <div className="border-t border-white/10 pt-3">
                            <p className="text-xs text-slate-300 mb-1.5">Option 2 · Upload your own <span className="text-[#a5b4fc]">slide deck</span> (.pptx)</p>
                            <div className="flex flex-wrap items-center gap-2">
                              <label data-testid={`pptx-upload-label-${c.id}`} className={`inline-flex items-center gap-2 h-9 px-3 rounded-md text-sm cursor-pointer border border-slate-600 bg-slate-800 hover:border-[#818CF8] text-white ${uploadingBlock === c.id ? "opacity-60 pointer-events-none" : ""}`}>
                                {uploadingBlock === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                                {uploadingBlock === c.id ? "Uploading…" : (pptx ? "Replace slide deck" : "Upload slide deck (.pptx)")}
                                <input data-testid={`pptx-upload-input-${c.id}`} type="file" accept=".pptx,.ppt,application/vnd.openxmlformats-officedocument.presentationml.presentation" className="hidden" onChange={(e) => { uploadPptx(c.id, e.target.files?.[0]); e.target.value = ""; }} />
                              </label>
                              {pptx && <span className="text-xs text-slate-400 truncate max-w-[180px]" title={pptx.filename}>{pptx.filename}</span>}
                              {pptx && <Button size="sm" variant="outline" data-testid={`pptx-remove-${c.id}`} onClick={() => deletePptx(c.id)} className="gap-1"><Trash2 className="w-3.5 h-3.5" /> Remove</Button>}
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-1.5">Tip: a published slides link always wins over an uploaded deck. Remove the link to show your deck.</p>
                          </div>
                        </div>
                      )}
                      {(() => {
                        const deckKey = `horizonquest_${track}_block${bi + 1}`;
                        const deckUrl = `/decks/${deckKey}.pptx`;
                        const images = deckImages[deckKey] || [];
                        if (url) {
                          return (
                            <>
                              <div className="aspect-video w-full rounded-lg overflow-hidden bg-black" data-testid={`slides-embed-${c.id}`}>
                                <iframe src={url} title={`Teaching slides block ${bi + 1}`} className="w-full h-full" allowFullScreen frameBorder="0" />
                              </div>
                              {isGuide && <p className="text-[11px] text-muted-foreground mt-1">Showing your custom slides.</p>}
                            </>
                          );
                        }
                        if (pptx) {
                          if (pptx.provider === "cloudinary") {
                            if (pptx.status === "ready" && (pptx.images || []).length) {
                              return (
                                <div data-testid={`slides-embed-${c.id}`}>
                                  <SlideCarousel images={pptx.images} title={`Block ${bi + 1} teaching slides`} />
                                  <p className="text-[11px] text-muted-foreground mt-1">Showing {isGuide ? "your uploaded" : "the class"} slide deck.</p>
                                </div>
                              );
                            }
                            return (
                              <div className="aspect-video w-full rounded-lg bg-slate-900/60 border border-white/10 flex flex-col items-center justify-center gap-2 text-sm text-muted-foreground" data-testid={`slides-embed-${c.id}`}>
                                <Loader2 className="w-6 h-6 animate-spin text-[#22D3EE]" />
                                Converting your slide deck into slides…
                              </div>
                            );
                          }
                          // legacy object-storage upload → Office viewer
                          const fileUrl = `${BACKEND}/api/decks/pptx/${c.id}.pptx?v=${pptx.version}`;
                          const officeSrc = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;
                          return (
                            <div data-testid={`slides-embed-${c.id}`}>
                              <div className="aspect-video w-full rounded-lg overflow-hidden bg-black">
                                <iframe src={officeSrc} title={`Teaching slides block ${bi + 1}`} className="w-full h-full" allowFullScreen frameBorder="0" />
                              </div>
                              <div className="flex items-center justify-between gap-2 mt-1.5 flex-wrap">
                                <p className="text-[11px] text-muted-foreground">Showing {isGuide ? "your uploaded" : "the class"} slide deck. If it doesn't load, use the link →</p>
                                <a href={fileUrl} target="_blank" rel="noopener noreferrer" data-testid={`pptx-open-${c.id}`} className="inline-flex items-center gap-1.5 text-xs text-[#22D3EE] hover:underline"><ExternalLink className="w-3.5 h-3.5" /> Open / Download slides</a>
                              </div>
                            </div>
                          );
                        }
                        if (images.length === 0) {
                          return <div className="aspect-video w-full rounded-lg bg-slate-900/60 border border-white/10 flex items-center justify-center text-center px-6 text-sm text-muted-foreground" data-testid={`slides-embed-${c.id}`}>{isGuide ? "No teaching slides for this block yet — click “Manage slides” to link your own slides or upload a .pptx deck." : "Your Guide hasn't added teaching slides for this block yet."}</div>;
                        }
                        return (
                          <div data-testid={`slides-embed-${c.id}`}>
                            <SlideCarousel images={images} title={`Block ${bi + 1} teaching slides`} deckUrl={deckUrl} />
                            <p className="text-[11px] text-muted-foreground mt-1">{isGuide ? "Showing the HorizonQuest starter deck. Click ‘Manage slides’ to use your own slides or upload a .pptx deck." : "Use the arrows to move through the slides."}</p>
                          </div>
                        );
                      })()}
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">{b.missions.map((m, i) => missionCard(m, i))}</div>

                    {(() => {
                      const blockDrills = drills.filter((d) => d.block_cp === c.id).sort((a, b) => (a.order || 0) - (b.order || 0));
                      if (!blockDrills.length) return null;
                      return (
                        <div className="hq-glass rounded-2xl p-4 mt-4 border border-white/10" data-testid={`drills-${c.id}`}>
                          <p className="text-sm font-medium flex items-center gap-2 mb-2"><Target className="w-4 h-4 text-[#34D399]" /> Skill Drills <span className="text-xs text-muted-foreground font-normal">· one per lesson, extra repetition practice</span></p>
                          <div className="flex flex-wrap gap-2">
                            {blockDrills.map((d) => {
                              const dp = progress[d.id];
                              const done = (dp?.score ?? 0) >= 60;
                              const dl = mLocked(d.id);
                              return (
                                <button key={d.id} data-testid={`drill-${d.id}`} onClick={() => dl ? toast.info(LOCK_MSG) : navigate(`/studio/${track}/${d.id}`)} title={dl ? LOCK_MSG : undefined}
                                  className={`inline-flex items-center gap-2 h-8 px-3 rounded-full text-xs border transition-colors ${dl ? "border-white/5 text-slate-500 opacity-60 cursor-not-allowed" : done ? "border-[#34D399]/50 bg-[#34D399]/10 text-[#34D399]" : "border-white/15 hover:bg-white/5"}`}>
                                  {dl ? <Lock className="w-3.5 h-3.5" /> : done ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Target className="w-3.5 h-3.5" />}
                                  {d.for_lesson ? <span className="text-[10px] font-mono-data opacity-70">L{d.for_lesson}</span> : null} {d.title}{dp?.score != null ? ` · ${dp.score}%` : ""}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}

                    {(() => {
                      const tasks = blockTasks.filter((t) => t.block_cp === c.id);
                      if (!tasks.length) return null;
                      return tasks.map((task) => {
                      const tp = progress[task.id];
                      const done = (tp?.score ?? 0) >= 60;
                      const tl = mLocked(task.id);
                      const extra = !!task.optional;
                      return (
                        <div key={task.id} className={`hq-glass rounded-2xl p-5 mt-4 border ${tl ? "border-white/5 opacity-60" : done ? (extra ? "border-[#34D399]/50" : "border-[#a5b4fc]/50") : extra ? "border-[#34D399]/25" : "border-[#a5b4fc]/25"}`} data-testid={`blocktask-card-${task.id}`}>
                          <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="min-w-0">
                              <h3 className="font-display text-lg flex items-center gap-2"><ClipboardCheck className={`w-4 h-4 ${extra ? "text-[#34D399]" : "text-[#a5b4fc]"}`} /> {task.title}</h3>
                              <p className="text-xs text-muted-foreground mt-1">{extra ? "Extra worksheet rehearsal — graded, but not required for the Checkpoint." : `Applied task using this block's skills${bi > 0 ? " + review" : ""}.`} {done ? "Passed ✓" : extra ? "" : "Required before the Checkpoint."}</p>
                            </div>
                            <div className="flex items-center gap-3">
                              {tp?.score != null && <span className="font-display text-2xl" style={{ color: done ? (extra ? "#34D399" : "#a5b4fc") : "#FB923C" }} data-testid={`blocktask-score-${task.id}`}>{tp.score}%</span>}
                              {tl ? (
                                <span className="flex items-center gap-2 text-xs text-slate-400" data-testid={`blocktask-locked-${task.id}`}><Lock className="w-4 h-4" /> {LOCK_MSG}</span>
                              ) : (
                              <Button data-testid={`blocktask-start-${task.id}`} onClick={() => navigate(`/studio/${track}/${task.id}`)} className={extra ? "bg-[#34D399] text-[#04121f] hover:bg-[#6EE7B7]" : "bg-[#a5b4fc] text-[#04121f] hover:bg-[#c7d2fe]"}>
                                {done ? "Redo Task" : "Start Task"}
                              </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                      });
                    })()}

                    <div className={`hq-glass rounded-2xl p-5 mt-4 border ${c.passed ? "border-[#34D399]/40" : "border-white/10"}`} data-testid={`checkpoint-card-${c.id}`}>
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                          <h3 className="font-display text-lg flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-[#22D3EE]" /> Checkpoint {bi + 1} Test</h3>
                          <p className="text-xs text-muted-foreground mt-1">{c.question_count} questions · pass {c.pass}% · 1 retake · {c.attempts_used}/{c.max_attempts} used{c.passed ? " · Passed ✓" : ""}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          {c.best_score != null && <span className="font-display text-2xl" style={{ color: c.passed ? "#34D399" : "#FB923C" }} data-testid={`checkpoint-score-${c.id}`}>{c.best_score}%</span>}
                          {!c.unlocked ? (
                            <span className={`flex items-center gap-2 text-xs ${c.guide_locked ? "text-[#FB923C]" : "text-slate-400"}`} data-testid={`checkpoint-locked-${c.id}`}><Lock className="w-4 h-4" /> {c.locked_reason}</span>
                          ) : noAttempts ? (
                            <Button data-testid={`checkpoint-review-${c.id}`} disabled variant="outline" className="opacity-60">No attempts left</Button>
                          ) : (
                            <Button data-testid={`checkpoint-take-${c.id}`} onClick={() => navigate(`/assessment/${c.id}`)} className="bg-[#22D3EE] text-[#04121f] hover:bg-[#67E8F9]">
                              {c.attempts_used === 0 ? "Take Checkpoint" : `Retake (${remaining} left)`}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </section>
                  {midTask && !isTrial && midTask.after_cp === c.id && <div className="mt-4"><TaskExamCard t={midTask} isGuide={isGuide} lockMsg={LOCK_MSG} navigate={navigate} testid="mid-task" /></div>}
                  {midTests.filter((mt) => mt.after_cp === c.id).map((mt) => {
                    const mp = progress[mt.id];
                    const guideLocked = tLocked(mt.id);
                    const locked = guideLocked || (!c.passed && !isGuide);
                    return (
                      <section key={mt.id} data-testid={`midtest-${mt.id}`} className={`hq-glass rounded-2xl p-5 mt-6 border ${mp?.score >= 70 ? "border-[#34D399]/50" : "border-[#FB923C]/40"}`}>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div className="w-12 h-12 rounded-xl bg-[#FB923C]/15 text-[#FB923C] flex items-center justify-center shrink-0"><ClipboardCheck className="w-6 h-6" /></div>
                            <div className="min-w-0">
                              <p className="text-[10px] uppercase tracking-widest text-[#FB923C]">Mid-unit · graded task test{isGuide ? " · Guide preview" : ""}</p>
                              <h3 className="font-display text-xl">{mt.title}</h3>
                              <p className="text-xs text-muted-foreground">{mt.tasks.length} formatting tasks in one real document · everything from Blocks 1–3 · +{mt.points} pts{mp?.score != null ? ` · Best: ${mp.score}%` : ""}</p>
                            </div>
                          </div>
                          {locked ? (
                            <span className={`flex items-center gap-2 text-xs shrink-0 ${guideLocked ? "text-[#FB923C]" : "text-slate-400"}`} data-testid={`midtest-locked-${mt.id}`}><Lock className="w-4 h-4" /> {guideLocked ? LOCK_MSG : `Pass Checkpoint ${bi + 1} first`}</span>
                          ) : (
                            <Button data-testid={`midtest-start-${mt.id}`} onClick={() => navigate(`/studio/${track}/${mt.id}`)} className="bg-[#FB923C] text-[#04121f] hover:bg-[#fdba74] shrink-0">{mp?.score != null ? "Improve your score" : "Start Task Test"}</Button>
                          )}
                        </div>
                      </section>
                    );
                  })}
                  </div>
                );
              })}

              {isTrial && <UpgradeCard user={user} title="Blocks 4–5, the Unit Test and every other unit" />}

              {unitTask && !isTrial && <TaskExamCard t={unitTask} isGuide={isGuide} lockMsg={LOCK_MSG} navigate={navigate} testid="unit-task" />}

              {unitMeta && !isTrial && (
                <section data-testid="unit-test-card" className={`hq-glass rounded-2xl p-5 border ${unitMeta.passed ? "border-[#34D399]/50" : "border-[#22D3EE]/40"}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-12 h-12 rounded-xl bg-[#22D3EE]/15 text-[#22D3EE] flex items-center justify-center shrink-0"><Award className="w-6 h-6" /></div>
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-widest text-[#22D3EE]">End of unit</p>
                        <h3 className="font-display text-xl">{unitMeta.title}</h3>
                        <p className="text-xs text-muted-foreground">{unitMeta.question_count} questions from every block in this unit · pass {unitMeta.pass}% · +{unitMeta.points} pts · {unitMeta.max_attempts > 1 ? <span className="text-[#34D399]">reassigned by your Guide · {unitMeta.attempts_used}/{unitMeta.max_attempts} used</span> : <span className="text-[#FB923C]">1 attempt (ask your Guide to reassign)</span>}{unitMeta.best_score != null ? ` · Best: ${unitMeta.best_score}%` : ""}</p>
                      </div>
                    </div>
                    {!unitMeta.unlocked ? (
                      <span className={`flex items-center gap-2 text-xs shrink-0 ${unitMeta.guide_locked ? "text-[#FB923C]" : "text-slate-400"}`} data-testid="unit-test-locked"><Lock className="w-4 h-4" /> {unitMeta.locked_reason}</span>
                    ) : unitMeta.attempts_used >= unitMeta.max_attempts ? (
                      <Button data-testid="unit-test-done" disabled variant="outline" className="opacity-60 shrink-0">{unitMeta.passed ? "Passed" : "Completed"} ({unitMeta.best_score}%)</Button>
                    ) : (
                      <Button data-testid="unit-test-start" onClick={() => navigate(`/assessment/${unitMeta.id}`)} className="bg-[#22D3EE] text-[#04121f] hover:bg-[#67E8F9] shrink-0">{unitMeta.attempts_used > 0 ? "Retake Unit Test" : "Take Unit Test"}</Button>
                    )}
                  </div>
                </section>
              )}

              {finalMeta && finalMeta.unlocked && (
                <div data-testid="final-exam-card" className="hq-glass rounded-2xl p-5 border border-primary/40 flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-12 h-12 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0"><Trophy className="w-6 h-6" /></div>
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-widest text-primary">Assigned by your Guide</p>
                      <h3 className="font-display text-xl">{finalMeta.title}</h3>
                      <p className="text-xs text-muted-foreground">{finalMeta.question_count} questions across all units · pass {finalMeta.pass}% · {finalMeta.max_attempts > 1 ? <span className="text-[#34D399]">reassigned · {finalMeta.attempts_used}/{finalMeta.max_attempts} used</span> : <span className="text-[#FB923C]">no retakes</span>}{finalMeta.best_score != null ? ` · Best: ${finalMeta.best_score}%` : ""}</p>
                    </div>
                  </div>
                  {finalMeta.attempts_used >= finalMeta.max_attempts ? (
                    <Button data-testid="final-exam-done" disabled variant="outline" className="opacity-60 shrink-0">Completed ({finalMeta.best_score}%)</Button>
                  ) : (
                    <Button data-testid="final-exam-start" onClick={() => navigate(`/assessment/final`)} className="bg-primary text-primary-foreground hover:bg-primary/90 shrink-0">{finalMeta.attempts_used > 0 ? "Retake Final Exam" : "Start Final Exam"}</Button>
                  )}
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
